/**
 * LA SESSIONE, LETTA UNA VOLTA SOLA.
 *
 * `GET /api/account/me` è già chiamato su **ogni rotta autenticata** (`TermsGate`, montato in
 * `_app.tsx`), e la sua risposta porta più di quello che serviva al muro dei termini: dentro c'è
 * `employeeId`, cioè l'anagrafica dipendente del chiamante sull'azienda in cui sta adesso.
 *
 * Quel campo esiste per una ragione scritta nel backend, e non è la velocità: dedurlo dal client è
 * **ambiguo**. `ErpEmployee` non ha il filtro d'azienda secco ma quello dell'albero holding, quindi
 * una lettura per `equals(user.id, …)` da un'azienda figlia restituisce anche le anagrafiche delle
 * altre società del gruppo — e chi prendeva la prima riga pescava quella sbagliata. Il sintomo è
 * datato nel commento del BE: timesheet a zero righe su 1074 reali, 28 agosto 2026.
 *
 * Questo hook esiste perché la chiave di cache sia **una**: `TermsGate` e `useMyEmployee` chiamano
 * lo stesso `queryKey`, quindi TanStack Query serve entrambi con una richiesta. Due chiavi
 * uguali-ma-diverse sarebbero due richieste per la stessa risposta, che è il difetto che questo
 * giro di lavoro sta chiudendo altrove.
 */
import { useQuery } from '@tanstack/react-query';
import { getAccountMe } from './api';

/** La chiave, esportata: chi la duplica scrivendola a mano paga una seconda richiesta. */
export const ACCOUNT_ME_KEY = ['account-me'] as const;

/**
 * La sessione corrente dal server, non dal JWT.
 *
 * `staleTime` di cinque minuti: è la stessa finestra di prima, e i campi che porta (termini
 * accettati, anagrafica collegata, superadmin) non cambiano mentre si naviga. Un cambio azienda
 * **ricarica la pagina** (`entraNellAzienda` → `window.location.replace('/')`), quindi la cache
 * non sopravvive al cambio e non c'è un caso in cui questo valore resti indietro.
 */
export function useAccountMe(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ACCOUNT_ME_KEY,
    queryFn: getAccountMe,
    staleTime: 5 * 60_000,
    retry: 1,
    /*
     * `enabled` serve a un solo chiamante, `useContestoAccesso`, che vive anche fuori dalla shell
     * (la palette ⌘K sta sulla pagina di login): li' una chiamata a `/account/me` senza token e' un
     * 401 in console su una schermata dove non c'e' niente da sbagliare. Tutti gli altri lo omettono.
     */
    enabled: options?.enabled ?? true,
  });
}
